import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import styles from "../../../styles/__withdrawFromWallet.module.scss";

import { fetchDefaultPayoutAccountReducer } from "../../../reducer/payoutAccount/fetchDefaultPayoutAccountReducer";
import { savePayoutAccountReducer } from "../../../reducer/payoutAccount/savePayoutAccountReducer";
import { submitSettlementRequestReducer } from "../../../reducer/settlement/submitSettlementRequestReducer";
import { fetchProviderSettlementsReducer } from "../../../reducer/settlement/fetchProviderSettlementsReducer";
import { fetchProviderDashboardReducer } from "../../../reducer/settlement/fetchProviderDashboardReducer";
import { formatCustom as tzCustom } from "../../../utils/datetime";

const PROVIDER_TYPE = "physiotherapy";

// Country dial codes + flags for the registered-mobile-number inputs
// (Zain Cash / QI Card). Mirrors the doctor login page.
const COUNTRIES = [
  { code: "+964", iso: "iq", name: "Iraq" },
  { code: "+91", iso: "in", name: "India" },
  { code: "+966", iso: "sa", name: "Saudi Arabia" },
  { code: "+971", iso: "ae", name: "UAE" },
  { code: "+965", iso: "kw", name: "Kuwait" },
  { code: "+974", iso: "qa", name: "Qatar" },
  { code: "+973", iso: "bh", name: "Bahrain" },
  { code: "+968", iso: "om", name: "Oman" },
  { code: "+962", iso: "jo", name: "Jordan" },
  { code: "+961", iso: "lb", name: "Lebanon" },
  { code: "+20", iso: "eg", name: "Egypt" },
  { code: "+90", iso: "tr", name: "Turkey" },
  { code: "+98", iso: "ir", name: "Iran" },
  { code: "+92", iso: "pk", name: "Pakistan" },
  { code: "+44", iso: "gb", name: "United Kingdom" },
  { code: "+1", iso: "us", name: "United States" },
  { code: "+49", iso: "de", name: "Germany" },
  { code: "+33", iso: "fr", name: "France" },
  { code: "+61", iso: "au", name: "Australia" },
];

// Split a stored full number ("+964770...") into its country code and national part.
const splitPhone = (fullVal) => {
  const val = String(fullVal || "").trim();
  if (!val) return { code: "+964", national: "" };
  const match = COUNTRIES.find((c) => val.startsWith(c.code));
  if (match) return { code: match.code, national: val.slice(match.code.length) };
  return { code: "+964", national: val.replace(/^\+/, "") };
};

// Payment methods supported by the settlement workflow.
const PAYMENT_METHODS = [
  { value: "zain_cash", labelKey: "withdraw.method.zain_cash", fallback: "Zain Cash" },
  { value: "qi_card", labelKey: "withdraw.method.qi_card", fallback: "Qi Card" },
  { value: "wire_transfer", labelKey: "withdraw.method.wire_transfer", fallback: "Wire Transfer" },
  { value: "pay_billing", labelKey: "withdraw.method.pay_billing", fallback: "PayBilling" },
];

const EMPTY_DETAILS = {
  // wire transfer
  bank_name: "",
  bank_branch_name: "",
  account_holder_name: "",
  account_number: "",
  iban: "",
  swift_bic_code: "",
  // zain cash
  wallet_number: "",
  // qi card
  card_number: "",
  // pay billing
  billing_account: "",
  // common
  holder_name: "",
};

const formatAmount = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("en-US");
};

const WithdrawFromWallet = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { defaultAccount, isSubmitting, isSaving, settlements, dashboards } = useSelector(
    (state) => state.payoutAccount
  );

  const [paymentMethod, setPaymentMethod] = useState("wire_transfer");
  const [orderType, setOrderType] = useState("normal");
  const [amount, setAmount] = useState("");
  const [details, setDetails] = useState({ ...EMPTY_DETAILS });
  const [saveAsDefault, setSaveAsDefault] = useState(true);
  const [errors, setErrors] = useState({});
  // Country dial code for the registered-mobile-number inputs (Zain Cash / QI Card).
  const [phoneCountry, setPhoneCountry] = useState("+964");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const selectedCountryData = COUNTRIES.find((c) => c.code === phoneCountry) || COUNTRIES[0];
  // Hold the form until the initial data has loaded, so the fields don't flip from the
  // default Wire Transfer layout to the saved method on refresh.
  const [bootstrapping, setBootstrapping] = useState(true);

  // Available balance for the SELECTED type is its net balance from the dashboard.
  const typeNetBalance = Math.max(
    0,
    Number(dashboards?.[orderType]?.net_balance || 0)
  );

  // Requests still awaiting approval / completion (block re-submission).
  // Only PAY_OUT settlements are withdrawal requests; a PAY_IN is the center
  // paying Tiryaq back (e.g. an admin-created settlement) and must never block
  // the center from submitting a withdrawal request.
  const isPayOut = (s) => (s?.direction || "pay_out") === "pay_out";
  const pendingSettlements = useMemo(
    () =>
      (settlements || []).filter(
        (s) => isPayOut(s) && (s.status === "pending" || s.status === "approved")
      ),
    [settlements]
  );
  const pendingAmount = useMemo(
    () =>
      pendingSettlements.reduce(
        (sum, s) => sum + Number(s.settlement_amount || 0),
        0
      ),
    [pendingSettlements]
  );
  const pendingForType = useMemo(
    () =>
      pendingSettlements
        .filter((s) => (s.order_type || "normal") === orderType)
        .reduce((sum, s) => sum + Number(s.settlement_amount || 0), 0),
    [pendingSettlements, orderType]
  );
  const hasPendingRequest = pendingSettlements.length > 0;

  // Withdrawal frequency limit (matches the backend): once every 2 months per order type. Any
  // non-rejected/cancelled settlement within the last 2 months blocks a new request; compute the
  // next eligible date to tell the provider proactively.
  const withdrawalCooldown = useMemo(() => {
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const recent = (settlements || [])
      // Only prior PAY_OUT (withdrawal) requests start the cooldown — a PAY_IN
      // (center paying Tiryaq back) must not block a new withdrawal request.
      .filter((s) => isPayOut(s))
      .filter((s) => (s.order_type || "normal") === orderType)
      .filter((s) => s.status !== "rejected" && s.status !== "cancelled")
      .filter((s) => s.created_at && new Date(s.created_at) >= twoMonthsAgo)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    if (!recent) return { blocked: false, nextDate: null };
    const next = new Date(recent.created_at);
    next.setMonth(next.getMonth() + 2);
    return { blocked: true, nextDate: next };
  }, [settlements, orderType]);

  const nextWithdrawalLabel = withdrawalCooldown.nextDate
    ? tzCustom(withdrawalCooldown.nextDate, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const availableForWithdrawal = Math.max(0, typeNetBalance - pendingForType);

  // The withdrawal amount is fixed to the available balance for the selected order
  // type (the input is read-only); keep it in sync as the type/balance changes.
  useEffect(() => {
    setAmount(availableForWithdrawal > 0 ? String(availableForWithdrawal) : "");
  }, [availableForWithdrawal]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      dispatch(fetchDefaultPayoutAccountReducer({ provider_type: PROVIDER_TYPE })),
      dispatch(fetchProviderSettlementsReducer({ provider_type: PROVIDER_TYPE })),
      dispatch(fetchProviderDashboardReducer({ provider_type: PROVIDER_TYPE, order_type: "normal" })),
    ]).finally(() => {
      if (mounted) setBootstrapping(false);
    });

    // Refresh when the admin approves/rejects/completes (real-time socket event).
    const onSettlementUpdate = () => {
      dispatch(fetchProviderSettlementsReducer({ provider_type: PROVIDER_TYPE }));
      dispatch(fetchProviderDashboardReducer({ provider_type: PROVIDER_TYPE, order_type: "normal" }));
    };
    window.addEventListener("settlement_updated", onSettlementUpdate);
    return () => {
      mounted = false;
      window.removeEventListener("settlement_updated", onSettlementUpdate);
    };
  }, [dispatch]);

  // Prefill the form from the saved default payout account (without overriding the
  // "save these details" checkbox, so the provider can freely toggle it).
  useEffect(() => {
    if (defaultAccount) {
      setPaymentMethod(defaultAccount.payment_method || "wire_transfer");
      const acct = defaultAccount.account_details || {};
      setDetails({ ...EMPTY_DETAILS, ...acct });
      const savedPhone = acct.wallet_number || acct.card_number;
      if (savedPhone) setPhoneCountry(splitPhone(savedPhone).code);
    }
  }, [defaultAccount]);

  // Close the country dropdown when clicking outside.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isCountryDropdownOpen && !e.target.closest("[data-country-dropdown]")) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCountryDropdownOpen]);

  const setField = (key, value) => {
    setDetails((prev) => ({ ...prev, [key]: value }));
  };

  // Fields shown per payment method.
  const methodFields = useMemo(() => {
    switch (paymentMethod) {
      case "zain_cash":
        return [
          { key: "wallet_number", labelKey: "withdraw.field.zain_cash_mobile", fallback: "Zain Cash registered mobile number", required: true, phone: true },
          { key: "holder_name", labelKey: "withdraw.field.holder_name", fallback: "Holder name", required: true },
        ];
      case "qi_card":
        return [
          { key: "card_number", labelKey: "withdraw.field.qi_card_mobile", fallback: "QI registered mobile number", required: true, phone: true },
          { key: "holder_name", labelKey: "withdraw.field.holder_name", fallback: "Holder name", required: true },
        ];
      case "pay_billing":
        return [
          { key: "billing_account", labelKey: "withdraw.field.billing_link", fallback: "PayBilling link", required: true },
          { key: "holder_name", labelKey: "withdraw.field.holder_name", fallback: "Holder name", required: true },
        ];
      case "wire_transfer":
      default:
        return [
          { key: "bank_name", labelKey: "withdraw.form.bank_name", fallback: "Bank name", required: true },
          { key: "bank_branch_name", labelKey: "withdraw.form.bank_branch_name", fallback: "Bank branch name", required: true },
          { key: "account_holder_name", labelKey: "withdraw.form.account_holder_name", fallback: "Account holder name", required: true },
          { key: "account_number", labelKey: "withdraw.form.account_number", fallback: "Account number", required: true },
          { key: "iban", labelKey: "withdraw.form.iban", fallback: "IBAN", required: false },
          { key: "swift_bic_code", labelKey: "withdraw.form.swift_bic", fallback: "SWIFT / BIC", required: false },
        ];
    }
  }, [paymentMethod]);

  const validate = () => {
    const e = {};
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      e.amount = t("withdraw.error.amount_required", "Enter a valid amount");
    } else if (amt > Number(availableForWithdrawal)) {
      e.amount = t("withdraw.error.amount_exceeds", "Amount exceeds withdrawable balance");
    }
    methodFields.forEach((f) => {
      if (f.required && !String(details[f.key] || "").trim()) {
        e[f.key] = t("withdraw.error.field_required", "This field is required");
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Build account_details with only the keys relevant to the selected method.
  const buildAccountDetails = () => {
    const out = {};
    methodFields.forEach((f) => {
      const v = String(details[f.key] || "").trim();
      if (v) out[f.key] = v;
    });
    return out;
  };

  const handleSubmit = async () => {
    if (hasPendingRequest || withdrawalCooldown.blocked) return;
    if (!validate()) return;

    const account_details = buildAccountDetails();

    if (saveAsDefault) {
      await dispatch(
        savePayoutAccountReducer({
          id: defaultAccount?.id,
          payment_method: paymentMethod,
          account_details,
          is_default: true,
        })
      );
    }

    const result = await dispatch(
      submitSettlementRequestReducer({
        order_type: orderType,
        payment_method: paymentMethod,
        account_details,
        settlement_amount: Number(amount),
        notes: t("withdraw.note.provider_request", "Withdrawal requested by provider"),
      })
    );

    if (submitSettlementRequestReducer.fulfilled.match(result)) {
      navigate("/physio_earning_payment");
    }
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") onNavigateToDashboard?.();
  };

  if (bootstrapping) {
    return (
      <PhysioDashboardLayout currentPage="physio_earning_payment" onNavigate={handleNavigate} onLogout={onLogout}>
        <div className={styles["patient-appointments-section"]}>
          <div className={styles["patient-section-header"]}>
            <div className={styles["patient-title"]}>
              <Link to="/physio_earning_payment" className={styles["backLink"]}>
                <HiOutlineArrowLeft />
              </Link>
              <h2>{t("withdrawfromWallet", "Withdraw Funds")}</h2>
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
            {t("loading", "Loading…")}
          </div>
        </div>
      </PhysioDashboardLayout>
    );
  }

  return (
    <PhysioDashboardLayout currentPage="physio_earning_payment" onNavigate={handleNavigate} onLogout={onLogout}>
      <div className={styles["patient-appointments-section"]}>
        <div className={styles["patient-section-header"]}>
          <div className={styles["patient-title"]}>
            <Link to="/physio_earning_payment" className={styles["backLink"]}>
              <HiOutlineArrowLeft />
            </Link>
            <h2>{t("withdrawfromWallet", "Withdraw Funds")}</h2>
          </div>
        </div>

        {/* Withdrawal frequency restriction notice */}
        <div
          role="note"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            margin: "0 0 1.25rem 0",
            padding: "0.85rem 1.1rem",
            borderRadius: "0.75rem",
            background: "#EFF6FF",
            border: "1px solid #BFDBFE",
            color: "#1E40AF",
            fontSize: "0.9rem",
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>ℹ️</span>
          <span>
            {t(
              "withdraw.frequency.notice",
              "You can submit a withdrawal request only once every 2 months. The amount is set automatically to your available balance."
            )}
          </span>
        </div>

        {hasPendingRequest && (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: "0 0 1.25rem 0",
              padding: "0.85rem 1.1rem",
              borderRadius: "0.75rem",
              background: "#FFF7E6",
              border: "1px solid #FFE2A8",
              color: "#8A5A00",
              fontSize: "0.9rem",
            }}
          >
            <span style={{ fontSize: "1.1rem" }}>⏳</span>
            <span>
              {t(
                "withdraw.pending.banner",
                "You already have a withdrawal request of IQD {{amount}} awaiting approval. You can submit a new request once it is completed."
              ).replace("{{amount}}", formatAmount(pendingAmount))}
            </span>
          </div>
        )}

        {/* Withdrawal cooldown notice — shows the next date a request can be submitted. */}
        {withdrawalCooldown.blocked && !hasPendingRequest && (
          <div
            role="note"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: "0 0 1.25rem 0",
              padding: "0.85rem 1.1rem",
              borderRadius: "0.75rem",
              background: "#FFF7E6",
              border: "1px solid #FFE2A8",
              color: "#8A5A00",
              fontSize: "0.9rem",
            }}
          >
            <span style={{ fontSize: "1.1rem" }}>🗓️</span>
            <span>
              {t(
                "withdraw.cooldown.banner",
                "You've already submitted a withdrawal request for this balance. Your next withdrawal request can be submitted on {{date}}."
              ).replace("{{date}}", nextWithdrawalLabel)}
            </span>
          </div>
        )}

        <div className={styles["patient-details"]}>
          <div className={styles["patient-profile-container"]}>
            <div className={styles["patient-profile-header"]}>
              <div className={styles["patient-profile-info"]}>
                <h2 className={styles["h2"]}>{t("wallet.balance.withdrawable", "Available for Withdrawal")}</h2>
                <p className={styles["h2"]}>
                  IQD <span>{formatAmount(availableForWithdrawal)}</span>
                </p>
                <small style={{ color: "#64748b" }}>
                  {orderType === "insured"
                    ? t("withdraw.order.insured", "Insured Orders")
                    : t("withdraw.order.normal", "Normal Orders")}
                </small>
              </div>
            </div>

            <div className={`${styles["patient-profile-header"]} ${styles["bg-2"]}`}>
              <div className={styles["patient-profile-info"]}>
                <h2 className={styles["h2"]}>{t("withdraw.wallet.pending", "Pending Approval")}</h2>
                <p className={styles["h2"]}>
                  IQD <span>{formatAmount(pendingAmount)}</span>
                </p>
              </div>
            </div>
          </div>

          <div className={styles["patient-info-table"]}>
            <div className={`container ${styles["withdraw-form"]}`}>
              <div className="row">
                {/* Order type */}
                <div className="col-md-12">
                  <div className="mb-4">
                    <label htmlFor="orderType" className="form-label">
                      {t("withdraw.form.order_type", "Settlement for")}
                    </label>
                    <select
                      className={`form-select ${styles["form-select"]}`}
                      id="orderType"
                      value={orderType}
                      disabled={hasPendingRequest}
                      onChange={(e) => setOrderType(e.target.value)}
                    >
                      <option value="normal">{t("withdraw.order.normal", "Normal Orders")}</option>
                    </select>
                  </div>
                </div>

                {/* Payment method */}
                <div className="col-md-12">
                  <div className="mb-4">
                    <label htmlFor="paymentMethod" className="form-label">
                      {t("withdraw.form.payment_method", "Payment method")}
                    </label>
                    <select
                      className={`form-select ${styles["form-select"]}`}
                      id="paymentMethod"
                      value={paymentMethod}
                      disabled={hasPendingRequest}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setErrors({});
                      }}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {t(m.labelKey, m.fallback)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Amount */}
                <div className="col-md-12">
                  <div className="mb-4">
                    <label className="form-label">
                      {t("withdraw.form.withdrawal_amount_label", "Total Withdrawal Amount (IQD)")}
                      <span className="text-danger"> *</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">IQD</span>
                      <input
                        type="number"
                        min="0"
                        className={`form-control ${styles["form-control"]}`}
                        placeholder="0"
                        value={amount}
                        disabled
                        readOnly
                      />
                    </div>
                    {errors.amount ? (
                      <small className="text-danger">{errors.amount}</small>
                    ) : (
                      <small className="form-text text-muted">
                        {t("withdraw.form.withdrawal_amount_hint", "The specified amount will be deducted from your wallet and deposited into your registered bank account.")}
                      </small>
                    )}
                  </div>
                </div>

                {/* Dynamic account fields */}
                {methodFields.map((f) => (
                  <div className="col-md-6" key={f.key}>
                    <div className="mb-4">
                      <label htmlFor={f.key} className="form-label">
                        {t(f.labelKey, f.fallback)}
                        {f.required && <span className="text-danger"> *</span>}
                      </label>
                      {f.phone ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "stretch",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            overflow: "visible",
                            background: "#fff",
                            opacity: hasPendingRequest ? 0.6 : 1,
                          }}
                        >
                          <div
                            data-country-dropdown
                            onClick={() => !hasPendingRequest && setIsCountryDropdownOpen((o) => !o)}
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "0 10px",
                              borderRight: "1px solid #e5e7eb",
                              cursor: hasPendingRequest ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <img
                              src={`https://flagcdn.com/24x18/${selectedCountryData.iso}.png`}
                              alt={selectedCountryData.name}
                              style={{ width: "24px", height: "18px", borderRadius: "2px", objectFit: "cover" }}
                            />
                            <span style={{ fontSize: "13px", fontWeight: 500 }}>{selectedCountryData.code}</span>
                            <ChevronDown size={14} />

                            {isCountryDropdownOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  width: "220px",
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  backgroundColor: "#fff",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "8px",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                  zIndex: 1000,
                                  marginTop: "4px",
                                }}
                              >
                                {COUNTRIES.map((country) => (
                                  <div
                                    key={`${f.key}-${country.code}-${country.iso}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const national = splitPhone(details[f.key]).national;
                                      setPhoneCountry(country.code);
                                      setField(f.key, national ? `${country.code}${national}` : "");
                                      setIsCountryDropdownOpen(false);
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      fontSize: "13px",
                                      backgroundColor: phoneCountry === country.code ? "#e6f7f7" : "#fff",
                                      borderBottom: "1px solid #f3f4f6",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = phoneCountry === country.code ? "#e6f7f7" : "#fff")}
                                  >
                                    <img src={`https://flagcdn.com/24x18/${country.iso}.png`} alt={country.name} style={{ width: "24px", height: "18px", borderRadius: "2px", objectFit: "cover" }} />
                                    <span style={{ fontWeight: 500 }}>{country.code}</span>
                                    <span style={{ color: "#6b7280" }}>{country.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <input
                            type="tel"
                            id={f.key}
                            placeholder="770 123 4567"
                            style={{ flex: 1, border: "none", outline: "none", padding: "10px 12px", background: "transparent", borderRadius: "0 8px 8px 0" }}
                            value={splitPhone(details[f.key]).national}
                            disabled={hasPendingRequest}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, "");
                              setField(f.key, digits ? `${phoneCountry}${digits}` : "");
                            }}
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          id={f.key}
                          className={`form-control ${styles["form-control"]}`}
                          value={details[f.key] || ""}
                          disabled={hasPendingRequest}
                          onChange={(e) => setField(f.key, e.target.value)}
                        />
                      )}
                      {errors[f.key] && (
                        <small className="text-danger">{errors[f.key]}</small>
                      )}
                    </div>
                  </div>
                ))}

                {/* Save as default */}
                <div className="col-md-12">
                  <div className="form-check mb-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="saveAsDefault"
                      checked={saveAsDefault}
                      disabled={hasPendingRequest}
                      onChange={(e) => setSaveAsDefault(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="saveAsDefault">
                      {t(
                        "withdraw.form.save_as_default",
                        "Save these details for future withdrawals"
                      )}
                    </label>
                  </div>
                </div>

                <div className="col-md-12 text-end">
                  <button
                    type="button"
                    className="btn-1"
                    onClick={() => navigate("/physio_earning_payment")}
                    disabled={isSubmitting || isSaving}
                  >
                    {t("withdraw.form.button.go_back", "Return to Previous Page")}
                  </button>
                  <button
                    type="button"
                    className="btn-outlined-1 ms-3"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isSaving || hasPendingRequest || withdrawalCooldown.blocked || Number(availableForWithdrawal) <= 0}
                  >
                    {hasPendingRequest
                      ? t("withdraw.form.button.pending", "Request Pending")
                      : isSubmitting || isSaving
                      ? t("withdraw.form.button.submitting", "Submitting...")
                      : t("withdraw.form.button.request_withdrawal", "Request Withdrawal")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PhysioDashboardLayout>
  );
};

export default WithdrawFromWallet;
